import React from "react";
import styled from "@emotion/styled";
import { useHistory } from "react-router-dom";
import { Button } from "reactstrap";
import { BiArrowBack } from "react-icons/bi";
import { FaCheck } from "react-icons/fa";

// Styled Components for layout and design
const PageContent = styled.div`
  max-width: 800px;
  margin: 2em auto;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 2em;
`;

const SectionHeader = styled.h2`
  font-size: 1.5rem;
  color: #049046;
  margin-bottom: 1em;
`;

const DetailsBlock = styled.div`
  margin-bottom: 2em;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 1em;
`;

const PreviewField = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5em" }}>
    <span style={{ fontWeight: "bold", color: "#333" }}>{label}:</span>
    <span>{value || "-"}</span>
  </div>
);

const QuestionBlock = styled.div`
  background-color: white;
  padding: 1.5em;
  margin-bottom: 20px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  position: relative;
`;

const RequiredTag = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: #f0f0f0;
  color: #888;
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: bold;
`;

const AnswerOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
`;

const AnswerButton = styled.button`
  background-color: ${(props) => (props.selected ? "#d4f8d4" : "#f0f0f0")};
  color: ${(props) => (props.selected ? "#198754" : "#333")};
  border: 1px solid ${(props) => (props.selected ? "#198754" : "#e0e0e0")};
  padding: 10px 20px;
  border-radius: 8px;
  cursor: default;
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
  width: ${(props) => (props.fullWidth ? "100%" : "500px")};

  & svg {
    visibility: ${(props) => (props.selected ? "visible" : "hidden")};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2em;
`;

const StyledButton = styled(Button)`
  width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const NoteText = styled.p`
  font-size: 0.9rem;
  color: #555;
  margin-top: 10px;
`;

const UploadButton = styled(Button)`
  width: 150px;
  margin-top: 10px;
`;

// Main Preview Component
const StepThree = ({
  questions,
  previewData = {}, // milestone and owner/supervisor details passed from Step One
  formType,
  save,
  createQuestionnaire,
  changeTab,
}) => {
  // Determine answer options based on formType
  let answerOptions = [];
  if (formType === "landscaping") {
    answerOptions = ["[1]Yes", "[0]No"];
  } else if (formType === "benchmarking") {
    answerOptions = [
      "[0] Not at all",
      "[1] Policy exists but it's either outdated and/or there are implementation challenges",
      "[2] There is considerable policy implementation but still room for improvement",
      "[3] Satisfactory progress with policy implementation",
    ];
  } else if (formType === "tracker") {
    answerOptions = ["Yes", "No"];
  }

  const history = useHistory();

  // Handler for saving and creating the questionnaire
  const handleSaveAndCreateQuestionnaire = async () => {
    await save();
    createQuestionnaire(questions);
    history.push("/forms");
  };

  // Handler for editing the form detailas (go back to Step One)
  const handleEditDetails = () => {
    changeTab("step1");
  };

  // Destructure preview data (with fallbacks)
  // const {
  //   milestoneName = "",
  //   stage = "",
  //   landscapingCategory = "",
  //   description = "",
  //   ownerName = "",
  //   supervisorName = "",
  // } = previewData;
  const milestoneName = previewData.milestoneName || previewData.name || "";
  const stage = previewData.stage || previewData.stage_id || "";
  const landscapingCategory = previewData.landscapingCategory || previewData.landscaping_category || "";
  const description = previewData.description || "";
  const ownerName = previewData.owner || "";
  const supervisorName = previewData.supervisor || "";

  const trackerStagesMap = {
    1: "Initiation",
    2: "Development",
    3: "Validation",
    4: "Approval",
    5: "Legislation",
    6: "Implementation",
  };

  const stageLabel = formType === "tracker" ? trackerStagesMap[stage] || stage : stage;

  return (
    <PageContent>
      {/* Milestone Details Section */}
      <SectionHeader>Milestone Details</SectionHeader>
      <DetailsBlock>
        {formType === "landscaping" ? (
          <>
            <PreviewField label="Milestone Name" value={milestoneName} />
            <PreviewField label="Landscaping Category" value={landscapingCategory} />
            <PreviewField label="Description" value={description} />
          </>
        ) : (
          <>
            <PreviewField label="Milestone Name" value={milestoneName} />
            <PreviewField label="Stage" value={stageLabel} />
          </>
        )}
      </DetailsBlock>

      {/* Questions Section */}
      <SectionHeader>Questions</SectionHeader>
      {questions.map((question, idx) => (
        <QuestionBlock key={idx}>
          {question.required && <RequiredTag>Required</RequiredTag>}
          <p
            style={{
              fontSize: "1.1rem",
              fontWeight: "400",
              marginBottom: "20px",
              marginTop: question.required ? "30px" : "0",
            }}
          >
            {`${idx + 1}. ${question.question_text}`}
          </p>
          <AnswerOptions>
            {answerOptions.map((option, index) => {
              let selected = false;
              if (formType === "landscaping" || formType === "benchmarking") {
                // Compare by splitting the option string (assumes response is stored as the text after the bracket)
                selected = question.question_response === option.split("]")[1].trim();
              } else {
                selected = question.question_response === option;
              }
              return (
                <AnswerButton key={index} selected={selected} fullWidth={formType !== "landscaping"}>
                  {option}
                </AnswerButton>
              );
            })}
          </AnswerOptions>
          {/* If evidence is required, show an Upload Evidence button (disabled in preview) */}
          {question.evidenceRequired && (
            <UploadButton color="primary" disabled>
              Upload Evidence
            </UploadButton>
          )}
          {/* Display the question note if available */}
          {question.question_note && <NoteText>Note: {question.question_note}</NoteText>}
        </QuestionBlock>
      ))}

      {/* Owner and Supervisor Section */}
      <SectionHeader>Owner and Supervisor</SectionHeader>
      <DetailsBlock>
        <PreviewField label="Owner" value={ownerName} />
        <PreviewField label="Supervisor" value={supervisorName} />
      </DetailsBlock>

      {/* Action Buttons */}
      <ButtonContainer>
        <StyledButton color="primary" onClick={handleEditDetails}>
          <BiArrowBack style={{ marginRight: "4px" }} />
          Edit
        </StyledButton>
        <StyledButton color="primary" onClick={handleSaveAndCreateQuestionnaire}>
          Save and Submit
          <FaCheck style={{ verticalAlign: "middle" }} />
        </StyledButton>
      </ButtonContainer>
    </PageContent>
  );
};

export default StepThree;